import React, { useEffect, useState } from 'react';
import { ChromePicker, ColorResult } from 'react-color';
import { Form, Input, Popover } from 'antd';
import { FormInstance } from 'antd/es/form';
import { DownOutlined } from '@ant-design/icons';
import './styles.scss';

interface ColorPickerProps {
  initialColor?: string;
  form: FormInstance;
  name: string;
  label?: string;
}

// Only ever used for the swatch shown next to an empty field - never written into the form.
const PREVIEW_FALLBACK = '#ffffff';

const ColorPicker: React.FC<ColorPickerProps> = ({ initialColor, form, name, label }) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  // Colour under the cursor while the picker is open, before `onChangeComplete` commits it.
  const [dragColor, setDragColor] = useState<string | undefined>(undefined);

  const fieldValue = Form.useWatch<string | undefined>(name, form);
  const color = dragColor ?? fieldValue ?? initialColor ?? PREVIEW_FALLBACK;

  useEffect(() => {
    // Only a colour the caller actually has is written into the form. This used to default to
    // #ffffff, and because the parent's `setFieldsValue` does not re-render this component
    // (`initialColor` stayed undefined), that placeholder overwrote colours loaded from the
    // server - every save then persisted #ffffff for every colour and blanked the theme.
    if (!initialColor) {
      return;
    }

    form.setFieldsValue({ [name]: initialColor });
  }, [form, name, initialColor]);

  const handleVisibleChange = () => setShowColorPicker(!showColorPicker);

  const handleColorChange = ({ hex }: ColorResult) => setDragColor(hex);

  const handleColorChangeComplete = ({ hex }: ColorResult) => {
    setDragColor(undefined);
    form.setFieldsValue({ [name]: hex });
  };

  const popoverContent = (
    <ChromePicker
      color={color}
      onChange={handleColorChange}
      onChangeComplete={handleColorChangeComplete}
      disableAlpha
    />
  );

  return (
    <Form.Item label={label}>
      <div className="color-picker">
        <Form.Item name={name} style={{ width: '100%' }}>
          <Input tabIndex={-1} readOnly />
        </Form.Item>
        <Popover
          content={popoverContent}
          placement="bottom"
          trigger="click"
          open={showColorPicker}
          onOpenChange={handleVisibleChange}
        >
          <button className="color-picker-trigger">
            <div className="color-picker-preview" style={{ backgroundColor: color }} />
            <DownOutlined className="color-picker-icon" />
          </button>
        </Popover>
      </div>
    </Form.Item>
  );
}

export default ColorPicker;
